import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';

type SecureMediaResolveResponse = {
  url?: string | null;
};

type SaveProfilePhotoResponse = {
  relationId?: number;
};

const SECURE_MEDIA_RESOLVE_URL = '/umbraco/api/SecureMedia/Resolve';
const PROFILE_PHOTO_RESOLVE_URL = '/umbraco/api/SecureMedia/ProfilePhotoUrl';
const SAVE_PROFILE_PHOTO_URL = '/umbraco/api/ImageStore/SaveProfilePhoto';

const getResolvedUrl = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const { url } = data as SecureMediaResolveResponse;
  return typeof url === 'string' && url.trim() ? url : null;
};

export const mediaProbeApi = createApi({
  reducerPath: 'mediaProbeApi',
  baseQuery,
  tagTypes: ['ProfilePhoto'],
  endpoints: (builder) => ({
    probePath: builder.query<{ resolvedUrl: string | null }, string>({
      async queryFn(path, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const result = await fetchWithBQ({
            url: `${SECURE_MEDIA_RESOLVE_URL}?path=${encodeURIComponent(path)}`,
            method: 'GET',
            credentials: 'include',
          });

          if (result.error) {
            return { error: result.error };
          }

          return { data: { resolvedUrl: getResolvedUrl(result.data) } };
        } catch (_e) {
          return { error: { status: 'CUSTOM_ERROR', error: 'Media resolution failed' } };
        }
      },
    }),
    probeProfilePhoto: builder.query<{ resolvedUrl: string | null }, number>({
      async queryFn(relationId, _queryApi, _extraOptions, fetchWithBQ) {
        if (!Number.isInteger(relationId) || relationId <= 0) {
          return { data: { resolvedUrl: null } };
        }

        try {
          const result = await fetchWithBQ({
            url: `${PROFILE_PHOTO_RESOLVE_URL}?relationId=${encodeURIComponent(relationId)}`,
            method: 'GET',
            credentials: 'include',
          });

          return { data: { resolvedUrl: result.error ? null : getResolvedUrl(result.data) } };
        } catch (_e) {
          return { data: { resolvedUrl: null } };
        }
      },
      providesTags: (_result, _error, relationId) => [{ type: 'ProfilePhoto', id: relationId }],
    }),
    saveProfilePhoto: builder.mutation<
      SaveProfilePhotoResponse,
      { relationId?: number; file: File }
    >({
      query: ({ relationId, file }) => {
        const body = new FormData();
        body.append('file', file);

        return {
          url:
            relationId == null
              ? SAVE_PROFILE_PHOTO_URL
              : `${SAVE_PROFILE_PHOTO_URL}?relationId=${encodeURIComponent(relationId)}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (_result, _error, { relationId }) =>
        relationId == null ? [] : [{ type: 'ProfilePhoto', id: relationId }],
    }),
  }),
});

export const {
  useProbePathQuery,
  useLazyProbePathQuery,
  useProbeProfilePhotoQuery,
  useSaveProfilePhotoMutation,
} = mediaProbeApi;
